import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  getContacts,
  getProperties,
  getPropertyStages,
  getPropertyStatuses,
} from 'src/services/client';


const PROPERTY_MANAGER_ROLE = 'Property Developer';
const STRATA_MANAGER_ROLE = 'Strata Manager';

interface DuplicatePropertyInput {
  id?: string;
  name: string;
  city: string;
  propertyManagerId: string;
}

export interface UsePropertyAPIReturn {
  propertyManagers: any[];
  strataManagers: any[];
  propertyStages: any[];
  propertyStatuses: any[];
  loader: boolean;
  user: any;
  setLoader: (loading: boolean) => void;
  findDuplicateProperty: (input: DuplicatePropertyInput) => Promise<boolean>;
}

export const usePropertyAPI = (): UsePropertyAPIReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [propertyStages, setPropertyStages] = useState<any[]>([]);
  const [propertyStatuses, setPropertyStatuses] = useState<any[]>([]);
  const [propertyManagers, setPropertyManagers] = useState<any[]>([]);
  const [strataManagers, setStrataManagers] = useState<any[]>([]);
  const [loader, setLoaderState] = useState<boolean>(false);

  const setLoader = (loading: boolean): void => {
    setLoaderState(loading);
  };

  const fetchStages = async (): Promise<void> => {
    try {
      const response: any = await getPropertyStages();
      const isOk: boolean = response.ok || response.status === 200;

      if (!isOk) {
        return;
      }

      const stages: any[] = await response.json();
      setPropertyStages(stages);
    } catch (err) {
      console.error('Failed to fetch property stages:', err);
    }
  };

  const fetchStatuses = async (): Promise<void> => {
    try {
      const response: any = await getPropertyStatuses();
      const isOk: boolean = response.ok || response.status === 200;

      if (!isOk) return;

      const statuses: any[] = await response.json();
      setPropertyStatuses(statuses);
    } catch (err) {
      console.error('Failed to fetch property statuses:', err);
    }
  };

  const fetchManagers = async (): Promise<void> => {
    try {
      const response: any = await getContacts();
      const isOk: boolean = response.ok || response.status === 200;

      if (!isOk) return;

      const allContacts: any[] = await response.json();

      const propertyDevelopers: any[] = allContacts.filter(
        (contact: any) =>
          contact.roles?.[0]?.role?.name === PROPERTY_MANAGER_ROLE
      );
      const strata: any[] = allContacts.filter(
        (contact: any) => contact.roles?.[0]?.role?.name === STRATA_MANAGER_ROLE
      );

      setPropertyManagers(propertyDevelopers);
      setStrataManagers(strata);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  useEffect(() => {
    fetchStages();
    fetchStatuses();
    fetchManagers();
  }, []);

  const findDuplicateProperty = async (
    input: DuplicatePropertyInput
  ): Promise<boolean> => {
    const userId: string | undefined = user?.id;
    const roleName: string | undefined = user?.roles?.[0]?.role?.name;

    if (!userId || !roleName) return false;

    try {
      const response: any = await getProperties(userId, roleName);
      const isOk: boolean = response.ok || response.status === 200;

      if (!isOk) return false;

      const allProperties: any[] = await response.json();
      const candidateName: string = input.name.toLowerCase().trim();
      const candidateCity: string = input.city.toLowerCase().trim();

      return allProperties.some((existing: any) => {
        const existingName: string =
          existing.name?.toLowerCase().trim() ?? '';
        const existingCity: string =
          existing.city?.toLowerCase().trim() ?? '';

        const nameMatches: boolean = existingName === candidateName;
        const cityMatches: boolean = existingCity === candidateCity;
        const managerMatches: boolean =
          existing.propertyManagerId === input.propertyManagerId;
        const isNotSelf: boolean = !input.id || existing.id !== input.id;

        return nameMatches && cityMatches && managerMatches && isNotSelf;
      });
    } catch (err) {
      console.error('Duplicate property check failed:', err);
      return false;
    }
  };

  return {
    propertyManagers,
    strataManagers,
    propertyStages,
    propertyStatuses,
    loader,
    user,
    setLoader,
    findDuplicateProperty,
  };
};
